import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CalendarClock, CheckCircle2, Mail, MessageCircle, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES } from "@/data/services";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "351926856052";

const BUDGETS = ["Under €1,000", "€1,000 – €5,000", "€5,000 – €15,000", "€15,000+", "To be discussed"];

const EMPTY_FORM = {
  full_name: "",
  email: "",
  company: "",
  website: "",
  service: "",
  budget: "",
  description: "",
  fax: "", // honeypot
};

const EMPTY_BOOKING = { name: "", email: "", date: "", time: "", subject: "", notes: "" };

const inputCls =
  "h-12 rounded-none border-white/15 bg-white/[0.03] px-4 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-empire-glow focus-visible:ring-offset-0";

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} role="alert" className="mt-2 text-xs text-empire-glow" data-testid={`${id}-error`}>
      {message}
    </p>
  ) : null;

function validateForm(f) {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Please enter a valid professional email.";
  if (!f.company.trim()) e.company = "Please enter your company name.";
  if (f.website.trim() && !/^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i.test(f.website.trim()))
    e.website = "Please enter a valid website URL.";
  if (!f.service) e.service = "Please select a service.";
  if (f.description.trim().length < 10) e.description = "Please describe your project (min. 10 characters).";
  else if (f.description.length > 500) e.description = "500 characters maximum.";
  return e;
}

function validateBooking(b) {
  const e = {};
  if (b.name.trim().length < 2) e.name = "Please enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(b.email.trim())) e.email = "Please enter a valid email.";
  if (!b.date) e.date = "Please pick a date.";
  if (!b.time) e.time = "Please pick a time.";
  if (!b.subject.trim()) e.subject = "Please tell us the subject.";
  return e;
}

function ContactCards() {
  const cards = [
    {
      icon: MessageCircle,
      title: "WhatsApp",
      line: "+351 926 856 052",
      note: "Fastest way to reach us",
      href: `https://wa.me/${WHATSAPP_NUMBER}`,
      external: true,
      testid: "contact-card-whatsapp",
    },
    {
      icon: Mail,
      title: "Email",
      line: "contact@4empire.fr",
      note: "For detailed briefs and documents",
      href: "mailto:contact@4empire.fr",
      external: false,
      testid: "contact-card-email",
    },
    {
      icon: CalendarClock,
      title: "Book a call",
      line: "Pick a time below",
      note: "Confirmed instantly via WhatsApp",
      href: "#booking",
      external: false,
      testid: "contact-card-booking",
    },
  ];
  return (
    <div className="mt-14 grid gap-6 md:grid-cols-3">
      {cards.map((c, i) => (
        <Reveal key={c.testid} delay={i * 0.08} className="h-full">
          <a
            href={c.href}
            {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            data-testid={c.testid}
            className="group flex h-full flex-col border border-white/10 bg-empire-coal/40 p-8 transition-colors duration-300 hover:border-empire-red/60"
          >
            <c.icon className="h-7 w-7 text-empire-red" strokeWidth={1.5} aria-hidden="true" />
            <h2 className="mt-6 font-anton text-lg uppercase tracking-wide">{c.title}</h2>
            <p className="mt-2 text-sm text-white">{c.line}</p>
            <p className="mt-1 text-sm text-empire-mist">{c.note}</p>
          </a>
        </Reveal>
      ))}
    </div>
  );
}

function ProjectForm() {
  const [params] = useSearchParams();
  const preselect = useMemo(() => {
    const slug = params.get("service");
    const match = SERVICES.find((s) => s.slug === slug);
    return match ? match.title : "";
  }, [params]);

  const [form, setForm] = useState({ ...EMPTY_FORM, service: preselect });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validateForm(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        website: form.website.trim() || null,
        service: form.service,
        budget: form.budget || null,
        description: form.description.trim(),
        fax: form.fax,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="border border-empire-red/50 bg-empire-coal/40 p-10" data-testid="contact-form-success" role="status">
        <CheckCircle2 className="h-10 w-10 text-empire-red" aria-hidden="true" />
        <h3 className="mt-6 font-anton text-2xl uppercase">Project received.</h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-empire-mist">
          Thank you, {form.full_name.split(" ")[0]}. Your project enquiry has been submitted — our
          team will review it and get back to you by email.
        </p>
        <button
          type="button"
          data-testid="contact-form-reset-button"
          onClick={() => {
            setForm({ ...EMPTY_FORM });
            setStatus("idle");
          }}
          className="mt-8 border border-white/20 px-6 py-3 font-grotesk text-xs uppercase tracking-[0.18em] text-white transition-colors hover:border-empire-red hover:text-empire-red"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="border border-white/10 bg-empire-coal/40 p-8 sm:p-10">
      {status === "error" && (
        <div className="mb-8 border border-empire-red/60 bg-empire-red/10 p-4 text-sm text-white" role="alert" data-testid="contact-form-error">
          Something went wrong while submitting your project. Please try again, or reach us
          directly on WhatsApp at +351 926 856 052.
        </div>
      )}

      {/* Honeypot — invisible to humans */}
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="fax">Fax</label>
        <input
          id="fax"
          name="fax"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.fax}
          onChange={set("fax")}
          data-testid="contact-honeypot-input"
        />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <Label htmlFor="full_name" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">
            Full name *
          </Label>
          <Input id="full_name" data-testid="contact-name-input" className={`mt-2 ${inputCls}`} value={form.full_name} onChange={set("full_name")} autoComplete="name" aria-describedby="full_name-error" />
          <FieldError id="full_name-error" message={errors.full_name} />
        </div>
        <div>
          <Label htmlFor="email" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">
            Professional email *
          </Label>
          <Input id="email" type="email" data-testid="contact-email-input" className={`mt-2 ${inputCls}`} value={form.email} onChange={set("email")} autoComplete="email" aria-describedby="email-error" />
          <FieldError id="email-error" message={errors.email} />
        </div>
        <div>
          <Label htmlFor="company" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">
            Company *
          </Label>
          <Input id="company" data-testid="contact-company-input" className={`mt-2 ${inputCls}`} value={form.company} onChange={set("company")} autoComplete="organization" aria-describedby="company-error" />
          <FieldError id="company-error" message={errors.company} />
        </div>
        <div>
          <Label htmlFor="website" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">
            Website <span className="normal-case text-white/30">(optional)</span>
          </Label>
          <Input id="website" data-testid="contact-website-input" className={`mt-2 ${inputCls}`} value={form.website} onChange={set("website")} placeholder="https://" aria-describedby="website-error" />
          <FieldError id="website-error" message={errors.website} />
        </div>
        <div>
          <Label className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">Service requested *</Label>
          <Select value={form.service} onValueChange={set("service")}>
            <SelectTrigger data-testid="contact-service-select" className={`mt-2 ${inputCls}`} aria-describedby="service-error">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#141414] text-white">
              {SERVICES.map((s) => (
                <SelectItem key={s.slug} value={s.title} data-testid={`contact-service-option-${s.slug}`}>
                  {s.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError id="service-error" message={errors.service} />
        </div>
        <div>
          <Label className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">
            Estimated budget <span className="normal-case text-white/30">(optional)</span>
          </Label>
          <Select value={form.budget} onValueChange={set("budget")}>
            <SelectTrigger data-testid="contact-budget-select" className={`mt-2 ${inputCls}`}>
              <SelectValue placeholder="Select a range" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#141414] text-white">
              {BUDGETS.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6">
        <Label htmlFor="description" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">
          Project description *
        </Label>
        <Textarea
          id="description"
          data-testid="contact-description-input"
          className={`mt-2 min-h-[140px] ${inputCls}`}
          value={form.description}
          onChange={set("description")}
          maxLength={500}
          aria-describedby="description-error description-count"
        />
        <div className="mt-2 flex items-center justify-between">
          <FieldError id="description-error" message={errors.description} />
          <span id="description-count" className="ml-auto text-xs text-empire-mist" data-testid="contact-description-count">
            {form.description.length}/500
          </span>
        </div>
      </div>

      <button
        type="submit"
        disabled={status === "sending"}
        data-testid="contact-submit-button"
        className="mt-8 inline-flex items-center gap-3 bg-empire-red px-10 py-4 font-grotesk text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-empire-glow disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "sending" ? "Sending..." : "Submit Your Project"}
        <Send className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </form>
  );
}

function BookingBlock() {
  const [b, setB] = useState(EMPTY_BOOKING);
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => {
    setB((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const confirm = (e) => {
    e.preventDefault();
    const errs = validateBooking(b);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const msg = [
      "Hello 4EMPIRE,",
      "",
      "I'd like to book a call.",
      "",
      `Name: ${b.name.trim()}`,
      `Email: ${b.email.trim()}`,
      `Preferred date: ${b.date}`,
      `Preferred time: ${b.time}`,
      `Subject: ${b.subject.trim()}`,
      b.notes.trim() ? `Notes: ${b.notes.trim()}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  return (
    <section id="booking" className="scroll-mt-24 bg-empire-coal py-24 lg:py-32" data-testid="booking-section">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <Reveal>
          <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">Book a call</p>
          <h2 className="mt-4 font-anton text-4xl uppercase leading-tight sm:text-5xl" data-testid="booking-heading">
            Pick a time. <span className="text-empire-red">We&apos;ll take it from here.</span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-empire-mist">
            Fill in your details and confirm — a pre-filled message opens directly in WhatsApp to
            our team at +351 926 856 052.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={confirm} noValidate data-testid="booking-form" className="mt-10 border border-white/10 bg-empire-black/50 p-8 sm:p-10">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label htmlFor="bk-name" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">Name *</Label>
                <Input id="bk-name" data-testid="booking-name-input" className={`mt-2 ${inputCls}`} value={b.name} onChange={set("name")} autoComplete="name" />
                <FieldError id="booking-name-error" message={errors.name} />
              </div>
              <div>
                <Label htmlFor="bk-email" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">Email *</Label>
                <Input id="bk-email" type="email" data-testid="booking-email-input" className={`mt-2 ${inputCls}`} value={b.email} onChange={set("email")} autoComplete="email" />
                <FieldError id="booking-email-error" message={errors.email} />
              </div>
              <div>
                <Label htmlFor="bk-date" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">Preferred date *</Label>
                <Input id="bk-date" type="date" data-testid="booking-date-input" className={`mt-2 ${inputCls} [color-scheme:dark]`} value={b.date} onChange={set("date")} />
                <FieldError id="booking-date-error" message={errors.date} />
              </div>
              <div>
                <Label htmlFor="bk-time" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">Preferred time *</Label>
                <Input id="bk-time" type="time" data-testid="booking-time-input" className={`mt-2 ${inputCls} [color-scheme:dark]`} value={b.time} onChange={set("time")} />
                <FieldError id="booking-time-error" message={errors.time} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="bk-subject" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">Subject *</Label>
                <Input id="bk-subject" data-testid="booking-subject-input" className={`mt-2 ${inputCls}`} value={b.subject} onChange={set("subject")} placeholder="e.g. Brand strategy for a fashion label" />
                <FieldError id="booking-subject-error" message={errors.subject} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="bk-notes" className="font-grotesk text-xs uppercase tracking-[0.18em] text-empire-mist">
                  Notes <span className="normal-case text-white/30">(optional)</span>
                </Label>
                <Textarea id="bk-notes" data-testid="booking-notes-input" className={`mt-2 min-h-[100px] ${inputCls}`} value={b.notes} onChange={set("notes")} />
              </div>
            </div>
            <button
              type="submit"
              data-testid="booking-confirm-button"
              className="mt-8 inline-flex items-center gap-3 bg-empire-red px-10 py-4 font-grotesk text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-empire-glow"
            >
              Confirm on WhatsApp <MessageCircle className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export default function Contact() {
  return (
    <main>
      <Seo
        title="Contact — 4EMPIRE | Let's Talk About Your Growth"
        description="Tell us about your project. Reach 4EMPIRE by WhatsApp, email or book a call — and submit your project brief directly through our form."
      />
      <section className="relative overflow-hidden bg-empire-black pb-20 pt-40 lg:pb-28 lg:pt-48" data-testid="contact-header">
        <div className="absolute inset-0 bg-grid" aria-hidden="true" />
        <div className="halo-red pointer-events-none absolute -right-32 top-0 h-[420px] w-[420px] rounded-full" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">Contact</p>
            <h1 className="mt-4 max-w-3xl font-anton text-5xl uppercase leading-[0.98] sm:text-6xl" data-testid="contact-heading">
              Let&apos;s talk about <span className="text-empire-red">your growth.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-empire-mist sm:text-lg">
              A project, a question, an ambition? Choose the channel that suits you — or send us
              your brief directly.
            </p>
          </Reveal>
          <ContactCards />
        </div>
      </section>

      <section className="bg-empire-black pb-24 lg:pb-32" data-testid="contact-form-section">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <h2 className="font-anton text-3xl uppercase sm:text-4xl" data-testid="contact-form-heading">
              Start your <span className="text-empire-red">project.</span>
            </h2>
            <p className="mt-3 text-sm text-empire-mist">
              Fields marked * are required. Your brief goes straight to our strategy team.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <ProjectForm />
          </Reveal>
        </div>
      </section>

      <BookingBlock />
    </main>
  );
}
